import type {ReactElement} from 'react';
import type {ContextualMenuGroup} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AI_VIDEO_PROMPT_BOX_ID, AiVideoFilterKey} from '@Panels/ai-video-panel/ai-video-panel.types';
import {stringToEnumOrNull} from '@Utils/string.util';
import {AiVideoSettingsPill} from './components/ai-video-settings-pill/ai-video-settings-pill';
import {AUDIO_OFF_VALUE, AUDIO_ON_VALUE, SPOKEN_TEXT_SETTING_VALUES} from './components/ai-video-settings-pill/ai-video-settings-pill.types';
import styles from './ai-video-settings-pills.module.scss';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';

interface AiVideoSettingsPillsProps {
  className?: string;
}

export function AiVideoSettingsPills({className = ''}: AiVideoSettingsPillsProps): ReactElement | null {
  const {state, actions} = useAiPromptBox(AI_VIDEO_PROMPT_BOX_ID);
  const contextualMenuGroups = state.contextualMenu ?? [];

  const audioGroupKey: string = AiVideoFilterKey.AUDIO;
  const textSettingGroupKey: string = AiVideoFilterKey.TEXT_SETTING;

  const isAudioOff = contextualMenuGroups.find((group) => group.key === audioGroupKey)?.items.find((item) => item.active)?.value === AUDIO_OFF_VALUE;

  const isSpokenTextSettingSelection = (group: ContextualMenuGroup, itemIndex: number): boolean => {
    if (group.key !== textSettingGroupKey) {
      return false;
    }
    const selectedValue = group.items[itemIndex]?.value;
    return selectedValue !== undefined && SPOKEN_TEXT_SETTING_VALUES.includes(selectedValue);
  };

  const turnAudioOn = (group: ContextualMenuGroup): ContextualMenuGroup => {
    if (group.key !== audioGroupKey) {
      return group;
    }
    return {
      ...group,
      items: group.items.map((item) => ({...item, active: item.value === AUDIO_ON_VALUE})),
    };
  };

  const isAudioOffSelection = (group: ContextualMenuGroup, itemIndex: number): boolean => {
    return group.key === audioGroupKey && group.items[itemIndex]?.value === AUDIO_OFF_VALUE;
  };

  const deselectSpokenTextSetting = (group: ContextualMenuGroup): ContextualMenuGroup => {
    if (group.key !== textSettingGroupKey) {
      return group;
    }
    return {
      ...group,
      items: group.items.map((item) => ({...item, active: item.active && !SPOKEN_TEXT_SETTING_VALUES.includes(item.value)})),
    };
  };

  const isDeselectableSelection = (group: ContextualMenuGroup, itemIndex: number): boolean => {
    return group.key === textSettingGroupKey && (group.items[itemIndex]?.active ?? false);
  };

  const selectItem = (groupIndex: number, itemIndex: number): void => {
    const group = contextualMenuGroups[groupIndex];
    const isDeselecting = isDeselectableSelection(group, itemIndex);
    let updatedGroups = [...contextualMenuGroups];
    updatedGroups[groupIndex] = {
      ...group,
      items: group.items.map((item, index) => ({
        ...item,
        active: index === itemIndex && !isDeselecting,
      })),
    };
    if (!isDeselecting && isSpokenTextSettingSelection(group, itemIndex)) {
      updatedGroups = updatedGroups.map(turnAudioOn);
    }
    if (isAudioOffSelection(group, itemIndex)) {
      updatedGroups = updatedGroups.map(deselectSpokenTextSetting);
    }
    actions.update({contextualMenu: updatedGroups});
  };

  const getFilterKeyForGroup = (group: ContextualMenuGroup): AiVideoFilterKey | null => {
    return stringToEnumOrNull(group.key, Object.values(AiVideoFilterKey));
  };

  const getPill = (group: ContextualMenuGroup, groupIndex: number): ReactElement | null => {
    const filterKey = getFilterKeyForGroup(group);
    if (!filterKey) {
      return null;
    }
    return (
      <AiVideoSettingsPill
        key={group.key}
        filterKey={filterKey}
        group={group}
        isAudioOff={isAudioOff}
        onSelectItem={(itemIndex): void => {
          selectItem(groupIndex, itemIndex);
          actions.update({promptBoxState: PromptBoxState.ACTIVE});
        }}
      />
    );
  };

  if (contextualMenuGroups.length === 0) {
    return null;
  }

  return <div className={`${styles.container} ${className}`}>{contextualMenuGroups.map(getPill)}</div>;
}
